// frontend/src/components/OwnerDashboard/WorkshopRatings.js
// Workshop ratings on the dashboard: what you still owe a rating for, and how
// the workshops you use are actually rated. Both come from the API — nothing
// here is placeholder copy.

import { useEffect, useState } from 'react';
import axios from 'axios';
import API_URL from '../../api/baseURL';
import { signal } from '../../theme/iparc';
import { IconWrench } from '../icons/Icons';

const Stars = ({ value = 0 }) => {
  const full = Math.floor(value);
  const half = value - full >= 0.5;
  return (
    <span className="flex gap-[1px]" aria-label={`${value.toFixed(1)} sur 5`}>
      {[0, 1, 2, 3, 4].map((i) => {
        const state = i < full ? 'full' : i === full && half ? 'half' : 'empty';
        return (
          <svg key={i} viewBox="0 0 24 24" className="w-3 h-3" aria-hidden="true">
            {state === 'half' && (
              <defs>
                <linearGradient id={`hs${i}`}>
                  <stop offset="50%" stopColor="#DFA23C" />
                  <stop offset="50%" stopColor="transparent" />
                </linearGradient>
              </defs>
            )}
            <path
              d="M12 2.5l2.9 5.9 6.5.95-4.7 4.58 1.11 6.47L12 17.35l-5.81 3.05 1.11-6.47-4.7-4.58 6.5-.95z"
              fill={state === 'full' ? '#DFA23C' : state === 'half' ? `url(#hs${i})` : 'none'}
              stroke="#DFA23C"
              strokeWidth="1.4"
              strokeLinejoin="round"
              opacity={state === 'empty' ? 0.35 : 1}
            />
          </svg>
        );
      })}
    </span>
  );
};

export default function WorkshopRatings({ token, pending = [], onRate, className = '' }) {
  const [top, setTop] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    axios
      .get(`${API_URL}/maintenance/top-rated-workshops`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => { if (alive) setTop(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (alive) setTop([]); })
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [token]);

  // Nothing to rate and no ratings yet — say so rather than render an empty box.
  const isEmpty = pending.length === 0 && top.length === 0;

  return (
    <div className={`flex flex-col rounded-2xl border border-[#241F1B] bg-gradient-to-b from-[#17140F] to-[#131110] overflow-hidden ${className}`}>
      <header className="flex items-center justify-between px-[18px] py-3.5 border-b border-[#241F1B]">
        <p className="text-[10.5px] uppercase tracking-[.16em] text-[#6E665C]">Ateliers &amp; évaluations</p>
        {pending.length > 0 && (
          <span
            className="px-1.5 py-[1px] rounded-full text-[10px] font-semibold tabular-nums"
            style={{ background: signal.warnTint, color: signal.warn }}
          >
            {pending.length} à noter
          </span>
        )}
      </header>

      {/* ── Awaiting your rating ─────────────────────────────────────── */}
      {pending.length > 0 && (
        <ul className="divide-y divide-[#1E1B18]">
          {pending.slice(0, 3).map((m) => {
            const workshop = m.workshop?.businessName || m.workshop?.name || 'Atelier';
            const plate = m.vehicle?.plateNumber || '';
            // The model field is serviceReason (serviceType does not exist).
            const service = m.serviceReason || m.description || 'Intervention';
            return (
              <li key={m._id} className="flex items-center gap-3 px-[18px] py-3">
                <span className="w-7 h-7 rounded-lg bg-[#241F1B] text-[#A79E90] flex items-center justify-center shrink-0">
                  <IconWrench className="w-[15px] h-[15px]" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[12.5px] text-[#F4F1EA] truncate">{workshop}</p>
                  <p className="text-[10.5px] text-[#6E665C] truncate">
                    {service}{plate && ` · ${plate}`}
                  </p>
                </div>
                <button
                  onClick={() => onRate?.(m)}
                  className="h-7 px-2.5 rounded-[7px] bg-gradient-to-b from-[#F4F1EA] to-[#CFCAC0]
                             text-[#141210] text-[11.5px] font-semibold shrink-0 hover:from-white"
                >
                  Noter
                </button>
              </li>
            );
          })}
          {pending.length > 3 && (
            <li className="px-[18px] py-2">
              <p className="text-[10.5px] text-[#5C554C]">
                +{pending.length - 3} autre{pending.length - 3 > 1 ? 's' : ''} en attente
              </p>
            </li>
          )}
        </ul>
      )}

      {/* ── Top rated ────────────────────────────────────────────────── */}
      {top.length > 0 && (
        <>
          {pending.length > 0 && <div className="h-px bg-[#241F1B]" />}
          <p className="px-[18px] pt-3 pb-1.5 text-[10px] uppercase tracking-[.14em] text-[#5C554C]">
            Les mieux notés
          </p>
          <ul className="divide-y divide-[#1E1B18]">
            {top.slice(0, 3).map((w, i) => {
              const avg = Number(w.avgRating) || 0;
              const n = Number(w.ratingCount) || 0;
              return (
                <li key={w._id || w.businessName || i} className="flex items-center gap-3 px-[18px] py-2.5">
                  <span className="w-5 text-[10.5px] text-[#5C554C] tabular-nums shrink-0">#{i + 1}</span>
                  <p className="text-[12.5px] text-[#C9C3B6] truncate flex-1 min-w-0">
                    {w.businessName || 'Atelier'}
                  </p>
                  <Stars value={avg} />
                  <span className="text-[11px] text-[#F4F1EA] tabular-nums shrink-0 w-6 text-right">
                    {avg.toFixed(1)}
                  </span>
                  <span className="text-[10px] text-[#5C554C] tabular-nums shrink-0">({n})</span>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {loaded && isEmpty && (
        <p className="px-[18px] py-6 text-[11.5px] text-[#5C554C] leading-[1.5]">
          Aucune évaluation pour le moment. Après une intervention terminée, vous
          pourrez noter l’atelier ici.
        </p>
      )}
    </div>
  );
}
